import { absoluteUrl } from "@/lib/site-origin";
import { fitTitle, fitDescription } from "@/lib/seo-head";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { stateKeywordsMeta } from "@/lib/page-keywords";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { getStateHero } from "@/data/state-heroes";
import { getState } from "@/data/states";
import { getMarketStats } from "@/lib/market-stats.functions";
import { getHousingMomentum } from "@/lib/housing-momentum.functions";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { FixFlipStatePage } from "@/components/site/FixFlipStatePage";
import { fixFlipStateFaqJsonLd, fixFlipStateMetaDescription } from "@/components/site/FixFlipStatePage.seo";
import { breadcrumbJsonLd, productHowToJsonLd, productArticleJsonLd } from "@/components/site/ProductPage.seo";
import { localBusinessJsonLd, serviceJsonLd, speakableJsonLd } from "@/lib/seo-schema";

const SLUG = "fix-and-flip-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG)!;

export const Route = createFileRoute("/fix-and-flip-loans/$state/")({
  loader: async ({ params }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    const [liveStats, housingMomentum, countyInsights] = await Promise.all([
      getMarketStats({ data: { stateSlug: params.state } }).catch(() => null),
      getHousingMomentum({ data: { stateAbbrev: state.abbrev, stateName: state.name } }).catch(() => null),
      getCountyInsights({ data: { stateAbbrev: state.abbrev, placeLabel: state.name } }).catch(() => null),
    ]);
    return { state, liveStats, housingMomentum, countyInsights };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: `Fix and flip loans | Simply Approved` }, { name: "robots", content: "noindex" }] };
    const url = `/${SLUG}/${params.state}`;
    const stateHero = getStateHero(params.state) ?? HERO;
    const title = fitTitle(`${state.name} Fix and Flip Loans | 90% LTP · 100% Rehab · 75% ARV | Simply Approved`);
    const description = fitDescription(fixFlipStateMetaDescription(state));
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: stateKeywordsMeta(PRODUCT.slug, state.name) },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: `https://dscrloanmarket.com${url}` },
        { property: "og:image", content: absoluteUrl(stateHero.src) },
        { property: "og:image:alt", content: stateHero.alt },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: absoluteUrl(stateHero.src) },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ],
      links: [
        { rel: "canonical", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "en-us", href: `https://dscrloanmarket.com${url}` },
        { rel: "preload", as: "image", href: stateHero.src },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd({ url, state: state.name })) },
        { type: "application/ld+json", children: JSON.stringify(serviceJsonLd({ url, serviceName: `Fix and Flip Loans in ${state.name}`, description, state: state.name })) },
        { type: "application/ld+json", children: JSON.stringify(speakableJsonLd(url)) },
        { type: "application/ld+json", children: JSON.stringify(fixFlipStateFaqJsonLd(state)) },
        { type: "application/ld+json", children: JSON.stringify(productHowToJsonLd(PRODUCT, url)) },
        { type: "application/ld+json", children: JSON.stringify(productArticleJsonLd(PRODUCT, url)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", url: "/" },
              { name: PRODUCT.shortName, url: `/${SLUG}` },
              { name: state.name, url },
            ]),
          ),
        },
      ],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { state, liveStats, housingMomentum, countyInsights } = Route.useLoaderData();
  return (
    <FixFlipStatePage product={PRODUCT} state={state} liveStats={liveStats} housingMomentum={housingMomentum} countyInsights={countyInsights} midSlot={<ProductReportCtaBanner productKey="flip" variant="full" locationLabel={state.name} />} />
  );
}
